import { memo } from "react"
import { _t } from "@cruisen/i18n"
import styles from "./styles.module.scss"
import { Stack } from "react-bootstrap"
import LikeButton from "../../atoms/LikeButton"
import { CruiseSearchResult, formatDate } from "@cruisen/react-shared"
import classNames from "classnames"
import { PortList } from "../../atoms/PortList"
import { ErrorHandledImage } from "../../atoms/ErrorHandledImage"
import { PriceDetails } from "../../atoms/PriceDetails"
import Tag from "../../atoms/Tag"
import { DiscountPriceDetails } from "../../atoms/DiscountPriceDetails"

const getDatesText = (startDate: Date, endDate: Date) => {
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return ""
  }

  return `${formatDate(startDate.toISOString(), "MMMM d")} - ${formatDate(
    endDate.toISOString(),
    "MMMM d",
  )}, ${startDate.getFullYear()}`
}

type CruiseSuggestionCardProps = {
  cruise: CruiseSearchResult
  tripId: string
  userId: string
  isSelected: boolean
  onClick: () => void
  testId: string
  isSeperatePage?: boolean
  onLikeButtonClick?: () => void
}

export const CruiseSuggestionCard = ({
  cruise,
  userId,
  tripId,
  isSelected,
  onClick,
  testId,
  isSeperatePage,
  onLikeButtonClick,
}: CruiseSuggestionCardProps) => {
  const datesText = getDatesText(cruise.startDate, cruise.endDate)
  const isCruiseLikedByAgent = cruise.likes.some((like) => like === userId)

  return (
    <div className={classNames(styles.wrapper, isSelected ? styles.isSelected : "")}>
      <div onClick={onClick} className={styles.pressable} data-testid={testId}>
        {/* <div className={styles.absoluteRow}>
          {cruise.cabinsScanStatus === "waiting" || cruise.cabinsScanStatus === "active" ? (
            <div className={styles.centerLoader}>
              <DefaultSpinner size="sm" />
            </div>
          ) : null}
        </div> */}
        <div className={styles.tagsList}>
          {cruise?.tags?.includes("priceDrop") ? <Tag variant="priceDrop" /> : null}
          {isCruiseLikedByAgent ? <Tag variant="agentPick" /> : null}
        </div>
        <div>
          <ErrorHandledImage
            uri={cruise.image}
            enableCaching
            className={styles.cruiseImage}
            errorMessage="Cruise image unavailable"
          />
        </div>
        <div
          className={classNames(isSeperatePage ? styles.blurViewSeperatePage : "", styles.blurView)}
        >
          <div className={styles.cardSection}>
            <div className={styles.cruiseLogoContainer}>
              <img src={cruise.lineLogoImage} className={styles.cruiseLogo} alt={cruise.lineName} />
            </div>
            <p className={styles.title}>{cruise.name}</p>
            <Stack gap={1}>
              {cruise.shipName && <p className={styles.datesText}>{cruise.shipName}</p>}
              {datesText !== "" && (
                <p className={styles.datesText} data-testid={`${testId}-dates-text`}>
                  {datesText}
                </p>
              )}
              <PortList ports={cruise.ports} />
            </Stack>
          </div>
          <div className={styles.divider} />
          <div className={styles.cardSection}>
            <div className={styles.rowBetween}>
              {cruise.price > 0 ? (
                <div>
                  {cruise.pricePP.discount ? (
                    <DiscountPriceDetails pricePP={cruise.pricePP} />
                  ) : (
                    <PriceDetails pricePP={cruise.pricePP} />
                  )}
                </div>
              ) : (
                <div className={styles.centerContent}>
                  <p>{_t("cruiseSuggestionCard.noCabinsAvailable")}</p>
                </div>
              )}
              <LikeButton
                userId={userId}
                tripId={tripId}
                tripSearchId={cruise.tripSearchId}
                docId={cruise.docId}
                cruiseLikes={cruise.likes}
                testId={`${testId}-like-button`}
                onClick={onLikeButtonClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function areEqual(prevProps: CruiseSuggestionCardProps, nextProps: CruiseSuggestionCardProps) {
  return (
    prevProps.cruise.likes.length === nextProps.cruise.likes.length &&
    prevProps.cruise.cabinsScanStatus === nextProps.cruise.cabinsScanStatus
  )
}

export default memo(CruiseSuggestionCard, areEqual)
